/**
 * Vehicle Overview Card Component
 * Consolidated display of VIN, title info, and key status indicators
 */

import React from 'react';
import { FileText, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

interface VehicleInfo {
  vin: string;
  titleState: string;
  titleDate: string | null;
  odometer: number | null;
  odometerUnit: string;
  titles?: Array<{
    state: string;
    date: string | null;
    odometer: number | null;
    odometerUnit: string;
    sequenceId: string;
    isHistoric: boolean;
  }>;
  brands: Array<{
    code: string;
    description: string;
    reportingEntity: string;
    date: string | null;
  }>;
  hasTheftRecord: boolean;
  hasSalvageRecord: boolean;
  hasFloodRecord: boolean;
  hasJunkRecord: boolean;
  hasRecords?: boolean;
  stolenRecords?: Array<{
    vin?: string;
    status?: string;
    lastEventDate?: string;
    state?: string;
  }>;
}

interface VehicleOverviewCardProps {
  vehicleInfo: VehicleInfo;
  className?: string;
}

const VehicleOverviewCard: React.FC<VehicleOverviewCardProps> = ({ vehicleInfo, className = '' }) => {
  // Check if there are actual title records (not just brands)
  const hasTitleRecords = vehicleInfo.titles && vehicleInfo.titles.length > 0;

  return (
    <div className={`p-6 glass-card-strong rounded-xl ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-[#64748b]">Current NMVTIS Title Information</h3>
            <div className="font-bold text-[#0f172a] text-xl font-mono mt-1">
              {vehicleInfo.vin}
            </div>
          </div>
        </div>
        {vehicleInfo.hasTheftRecord && (
          <div className="flex items-center gap-2 px-3 py-2 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H15L14 3H10L9 5H6.5C5.84 5 5.28 5.42 5.08 6.01L3 12V20C3 20.55 3.45 21 4 21H5C5.55 21 6 20.55 6 20V19H18V20C18 20.55 18.45 21 19 21H20C20.55 21 21 20.55 21 20V12L18.92 6.01ZM6.5 16C5.67 16 5 15.33 5 14.5C5 13.67 5.67 13 6.5 13C7.33 13 8 13.67 8 14.5C8 15.33 7.33 16 6.5 16ZM17.5 16C16.67 16 16 15.33 16 14.5C16 13.67 16.67 13 17.5 13C18.33 13 19 13.67 19 14.5C19 15.33 18.33 16 17.5 16ZM5 11L6.5 6.5H17.5L19 11H5Z" fill="#DC2626"/>
              <path d="M19 14L17 10L15 14H19Z" fill="#FEF2F2" stroke="#DC2626" strokeWidth="0.5"/>
              <circle cx="17" cy="12.5" r="0.4" fill="#DC2626"/>
              <path d="M17 11V11.8" stroke="#DC2626" strokeWidth="0.6" strokeLinecap="round"/>
            </svg>
            <div>
              <div className="text-xs font-bold text-[#dc2626] uppercase tracking-wide">Stolen</div>
              <div className="text-xs font-bold text-[#dc2626] uppercase tracking-wide">Vehicle</div>
            </div>
          </div>
        )}
      </div>

      {/* Title Information Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-black/[0.08]">
        <div>
          <div className="text-xs text-[#64748b] mb-1">Date Issued</div>
          <div className="text-sm font-medium text-[#0f172a]">
            {vehicleInfo.titleDate ? new Date(vehicleInfo.titleDate).toLocaleDateString() : 'N/A'}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#64748b] mb-1">State</div>
          <div className="text-sm font-medium text-[#0f172a]">{vehicleInfo.titleState}</div>
        </div>
        <div>
          <div className="text-xs text-[#64748b] mb-1">Odometer</div>
          <div className="text-sm font-medium text-[#0f172a]">
            {vehicleInfo.odometer
              ? `${vehicleInfo.odometer.toLocaleString()} ${vehicleInfo.odometerUnit === 'M' ? 'miles' : 'km'}`
              : 'EXEMPT'}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#64748b] mb-1">VIN</div>
          <div className="text-sm font-medium text-[#0f172a] font-mono">{vehicleInfo.vin}</div>
        </div>
      </div>

      {/* Status Indicators Grid */}
      <div className="grid grid-cols-3 gap-3">
        {/* Theft Check */}
        <div className="flex items-center space-x-2">
          {vehicleInfo.hasTheftRecord ? (
            <XCircle className="w-5 h-5 text-[#dc2626] flex-shrink-0" />
          ) : !hasTitleRecords ? (
            <AlertTriangle className="w-5 h-5 text-[#d97706] flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-[#16a34a] flex-shrink-0" />
          )}
          <div>
            <div className="text-xs text-[#64748b]">Theft</div>
            <div className={`text-sm font-semibold ${
              vehicleInfo.hasTheftRecord ? 'text-[#dc2626]' :
              !hasTitleRecords ? 'text-[#d97706]' : 'text-[#16a34a]'
            }`}>
              {vehicleInfo.hasTheftRecord ? 'Reported' : !hasTitleRecords ? 'No Records' : 'Clear'}
            </div>
          </div>
        </div>

        {/* Title Status */}
        <div className="flex items-center space-x-2">
          {!hasTitleRecords ? (
            <AlertTriangle className="w-5 h-5 text-[#d97706] flex-shrink-0" />
          ) : vehicleInfo.hasSalvageRecord ? (
            <XCircle className="w-5 h-5 text-[#dc2626] flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-[#16a34a] flex-shrink-0" />
          )}
          <div>
            <div className="text-xs text-[#64748b]">Title</div>
            <div className={`text-sm font-semibold ${
              !hasTitleRecords ? 'text-[#d97706]' :
              vehicleInfo.hasSalvageRecord ? 'text-[#dc2626]' : 'text-[#16a34a]'
            }`}>
              {!hasTitleRecords ? 'No Records' : vehicleInfo.hasSalvageRecord ? 'Salvage' : 'Clean'}
            </div>
          </div>
        </div>

        {/* Flood Damage */}
        <div className="flex items-center space-x-2">
          {vehicleInfo.hasFloodRecord ? (
            <XCircle className="w-5 h-5 text-[#dc2626] flex-shrink-0" />
          ) : !hasTitleRecords ? (
            <AlertTriangle className="w-5 h-5 text-[#d97706] flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-[#16a34a] flex-shrink-0" />
          )}
          <div>
            <div className="text-xs text-[#64748b]">Flood</div>
            <div className={`text-sm font-semibold ${
              vehicleInfo.hasFloodRecord ? 'text-[#dc2626]' :
              !hasTitleRecords ? 'text-[#d97706]' : 'text-[#16a34a]'
            }`}>
              {vehicleInfo.hasFloodRecord ? 'Reported' : !hasTitleRecords ? 'No Records' : 'None'}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default VehicleOverviewCard;
